"use client"

import styled from "@emotion/styled"
import { GetPageByRouteQuery } from "@eurodrive/apollo"
import { CustomBreadcrumbs } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import { extractProperty, getBreadCrumbs } from "@eurodrive/utils"
import { Typography } from "@mui/material"
import Image from "next/image"
import CareerForm from "./CareerForm"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const InnerLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 488px;
    gap: 64px;
  }
  gap: 43px;
  padding-bottom: 64px;
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: 0 20px;
  }
`

const InfoBox = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
`

const Chip = styled.div`
  display: flex;
  width: fit-content;
  min-width: 103px;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: #f3f3f3;
`

const DetailsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const DescriptionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RequirementsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RequirementsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledRequirementsItem = styled(Typography)`
  ul {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    li {
      list-style: none;
      position: relative;
      padding-left: 24px;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0.125em;
        width: 16px;
        height: 16px;
        background-image: url("/icons/li.svg");
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;
      }
    }
  }
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
`

export default function IndividualCareer({
  data,
  locale,
}: {
  data: GetPageByRouteQuery
  locale: string
}) {
  const page = data.page
  const breadCrumbs = getBreadCrumbs(data, locale)
  const jobTitle = extractProperty(page, "jobTitle")
  const jobNumber = extractProperty(page, "jobNumber")
  const city = extractProperty(page, "city")
  const labeling = extractProperty(page, "labeling")
  const shortDescription = extractProperty(page, "shortDescription")
  const fullDescription = extractProperty(page, "fullDescription")
  const businessHours = extractProperty(page, "businessHours")
  const jobRequirements = extractProperty(page, "jobRequirements")
  return (
    <Container>
      <StyledLayout>
        <CustomBreadcrumbs breadcrumbs={breadCrumbs} />
        <InnerLayout>
          <ContentLayout>
            <InfoBox>
              <Chip>
                <Typography variant="body2">{labeling}</Typography>
              </Chip>
              <Typography variant="h1" component="h1">{jobTitle}</Typography>
              <DetailsRow>
                <DetailRow>
                  <Typography variant="body2">מספר המשרה:</Typography>
                  <Typography variant="body2">{jobNumber}</Typography>
                </DetailRow>
                <DetailRow>
                  <Image
                    src={"/icons/mapIcon.svg"}
                    width={18}
                    height={18}
                    alt=""
                    aria-hidden="true"
                  />
                  <Typography variant="body2">{city}</Typography>
                </DetailRow>
              </DetailsRow>
              <Typography variant="body2">{shortDescription}</Typography>
            </InfoBox>
            <DescriptionBox>
              <Typography variant="h2" component="h2">התפקיד כולל:</Typography>
              <StyledTypography
                variant="body2"
                dangerouslySetInnerHTML={{ __html: fullDescription }}
              />
              <StyledTypography
                variant="body2"
                dangerouslySetInnerHTML={{ __html: businessHours }}
              />
            </DescriptionBox>
            <RequirementsBox>
              <Typography variant="h2" component="h2">דרישות התפקיד:</Typography>
              <StyledRequirementsItem
                variant="body2"
                dangerouslySetInnerHTML={{ __html: jobRequirements }}
              />
            </RequirementsBox>
          </ContentLayout>
          <div>
            <Typography variant="h2" component="h2" style={{ marginBottom: 24 }}>הגש מועמדות</Typography>
            <CareerForm
              jobTitle={jobTitle}
              jobNumber={jobNumber}
              jobCity={city}
              jobCategory={labeling}
              locale={locale}
            />
          </div>
        </InnerLayout>
      </StyledLayout>
    </Container>
  )
}
